import React, { useMemo, useState } from 'react';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  CirclePlus,
  ExternalLink,
  FileText,
  Info,
  MapPinned,
  Phone,
  ShieldAlert,
  Tags,
} from 'lucide-react';
import { getGuestDayBackground } from '../guestDayBackgrounds';

const ACTION_SPEC_SOURCES = [
  'Guest card actions: phone, map, save-to-plan, note.',
  'Companion action paths: website, map, phone, booking link, or clear practical instruction.',
  'Day slots: suggested, locked, cleared, with alternatives.',
  'Analytics: added_to_trip, removed_or_skipped, website_click, directions_click, contact_or_booking_click.',
];

const THEME_TAGS = [
  { id: 'classic_muskoka', label: 'Classic' },
  { id: 'rain_friendly', label: 'Rainy' },
  { id: 'date_night', label: 'Date' },
  { id: 'active_outdoors', label: 'Active' },
  { id: 'flavour_day', label: 'Food' },
  { id: 'family_easy', label: 'Family' },
];

const DAY_TABS = [
  { id: 'fri', label: 'Fri', title: 'Arrival' },
  { id: 'sat', label: 'Sat', title: 'Bracebridge day' },
  { id: 'sun', label: 'Sun', title: 'Slow morning' },
];

const BRIEF = {
  tripName: 'Alex - Bracebridge Weekend',
  area: 'Bracebridge',
  party: '2 adults, 2 kids',
  pace: 'relaxed pace',
  hasDog: false,
  season: 'autumn',
  tripTheme: 'Classic Muskoka weekend',
  dayThemeLine: [
    'A slow Bracebridge day, mostly on foot.',
    'Downtown in the middle of it, and a good pint at the end.',
  ],
};

const STOPS = [
  {
    id: 'library',
    placeMasterId: 'pocket-host-place-osm-0349',
    title: 'Bracebridge Library',
    daypart: 'Midday',
    bucket: 'Creative',
    dossierState: 'Published card',
    fitSentences: [
      'Start indoors while the day warms up.',
      'Air conditioning, free wifi, and a lounge area you can actually sit in.',
      "Puzzles and board games if you've got an hour to kill.",
    ],
    isInitiallyAdded: true,
    mapAvailable: true,
    callAvailable: true,
    websiteAvailable: true,
    headsUp:
      'Drop-in programs are cardholder-only; for visitors, keep this to lounge time, puzzles, and board games.',
    safetyFacts: [],
    guestUncertaintyNotes: [],
    conditionalUncertaintyNotes: [
      {
        id: 'library-dog',
        condition: 'hasDog',
        text: 'Dog policy is unverified in the checked dossier facts.',
      },
    ],
    planningFacts: [
      {
        key: 'what_to_know_candidate',
        value:
          'Library drop-in programs are cardholder-only; visitors can use the lounge, puzzles, and board games.',
      },
    ],
    sourceFacts: [
      'Bracebridge Library (pocket-host-place-osm-0349) is the Composer-eligible published card with trusted coordinates.',
      'Coulson Family Bracebridge Library (pocket-host-place-00367) is the unpublished duplicate candidate.',
      'Manual source review identified the visitor constraint: drop-in programs are cardholder-only; visitors can use the lounge, puzzles, and board games.',
      'OSM source row lists Bracebridge Library with website and phone metadata.',
    ],
    provenance: 'Official library review, OSM library row, and local Composer publication check.',
    publicationStatus: 'Published card available; duplicate pair should be queued for duplicate cleanup.',
  },
  {
    id: 'griffin',
    placeMasterId: 'pocket-host-place-00062',
    title: 'The Griffin Gastropub',
    daypart: 'Evening',
    bucket: 'Flavour',
    dossierState: 'Rich dossier',
    fitSentences: [
      "End the day up Chancery Lane, a pedestrian walkway you'd miss if you didn't know it was there.",
      'Ontario pours and a rotating tap list, plus shareables and mains if the walking has caught up with you.',
    ],
    isInitiallyAdded: true,
    mapAvailable: true,
    callAvailable: true,
    websiteAvailable: true,
    headsUp:
      'Seating is first come, first served; municipal parking is listed on Manitoba Street, Dominion Street, or the Courthouse lot.',
    safetyFacts: [],
    guestUncertaintyNotes: [],
    conditionalUncertaintyNotes: [
      {
        id: 'griffin-dog',
        condition: 'hasDog',
        text: 'Dog policy is unverified in the checked dossier facts.',
      },
    ],
    planningFacts: [
      {
        key: 'seating_constraint',
        value: 'Seating is first come, first served.',
      },
      {
        key: 'parking_instruction',
        value:
          'Municipal parking is listed on Manitoba Street, Dominion Street, or the Courthouse lot after 5 p.m. weekdays and all day weekends.',
      },
    ],
    sourceFacts: [
      "Beer-focused pub with Ontario beers and a dedicated What's Pouring page.",
      'Broad pub menu with shareables, salads, mains, and smaller appetites.',
      'Located at the top of Chancery Lane, a pedestrian walkway behind 32 Dominion Street.',
    ],
    provenance: 'Official business website plus enriched card attributes.',
    publicationStatus: 'Published card fixture source.',
  },
  {
    id: 'chucks',
    placeMasterId: 'pocket-host-place-00034',
    title: "Chuck's Roadhouse",
    daypart: 'Evening',
    bucket: 'Flavour',
    dossierState: 'Thin dossier',
    fitSentences: [
      'This is the thin-card case: the place record and official location page exist, but there is not much curated detail yet.',
      'Keep the copy short and confirm current hours before building the evening around it.',
    ],
    isInitiallyAdded: false,
    mapAvailable: true,
    callAvailable: false,
    websiteAvailable: true,
    headsUp:
      'Thin dossier. Confirm current hours and details before making this the only evening plan.',
    safetyFacts: [],
    guestUncertaintyNotes: [
      'Only master/source facts were available in the checked fixture data.',
    ],
    conditionalUncertaintyNotes: [
      {
        id: 'chucks-dog',
        condition: 'hasDog',
        text: 'Dog policy is unverified in the checked dossier facts.',
      },
    ],
    planningFacts: [
      {
        key: 'thin_dossier',
        value: 'Only master/source facts were available in the checked fixture data.',
      },
    ],
    sourceFacts: [
      'Master record classifies the place under Food and Drink.',
      'Official chain/location page is listed for the Bracebridge location.',
    ],
    provenance: 'Master place row and official chain/location page.',
    publicationStatus: 'Published card fixture source; sparse attributes.',
  },
];

function getGuestUncertaintyNotes(stop) {
  const conditionalNotes = stop.conditionalUncertaintyNotes
    .filter(note => note.condition !== 'hasDog' || BRIEF.hasDog)
    .map(note => note.text);
  return [...stop.guestUncertaintyNotes, ...conditionalNotes];
}

function ActionButton({ children, icon: Icon, pressed = false, onClick }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={`inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${
        pressed
          ? 'border-[#8B6F47]/30 bg-[#8B6F47]/10 text-[#6F5638]'
          : 'border-[#E5DFD6] bg-white text-[#2C3E3A] hover:border-[#8B6F47]/40 hover:bg-[#FAF8F5]'
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}

function ThemeTags({ selectedTheme, onSelectTheme }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {THEME_TAGS.map(tag => {
        const selected = selectedTheme === tag.id;
        return (
          <button
            key={tag.id}
            type="button"
            onClick={() => onSelectTheme(tag.id)}
            className={`inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${
              selected
                ? 'border-[#8B6F47] bg-[#8B6F47] text-white'
                : 'border-[#E5DFD6] bg-white text-[#5A5A5A] hover:border-[#8B6F47]/40'
            }`}
          >
            <Tags className="h-3.5 w-3.5" />
            {tag.label}
          </button>
        );
      })}
    </div>
  );
}

function FitCopy({ sentences }) {
  return (
    <div className="mt-2 space-y-1 text-[15px] leading-7 text-[#5A5A5A]">
      {sentences.map(sentence => <p key={sentence}>{sentence}</p>)}
    </div>
  );
}

function QuietLine({ label, children, tone = 'default' }) {
  const toneClass =
    tone === 'safety'
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-[#E5DFD6] bg-[#FAF8F5] text-[#6F5638]';

  if (!children) return null;

  return (
    <p className={`mt-3 rounded-md border px-3 py-2 text-xs leading-5 ${toneClass}`}>
      <span className="font-semibold">{label}: </span>
      {children}
    </p>
  );
}

function AdminFactList({ items }) {
  return (
    <ul className="space-y-2 text-sm leading-6 text-[#5A5A5A]">
      {items.map(item => (
        <li key={item} className="flex gap-2">
          <CheckCircle2 className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-[#8B6F47]" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function AdminDetails({ stop }) {
  return (
    <div className="mt-4 grid gap-4 border-t border-[#E5DFD6] pt-4 lg:grid-cols-2">
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-[#8B6F47]">Source facts</h4>
        <div className="mt-2">
          <AdminFactList items={stop.sourceFacts} />
        </div>
      </section>
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-[#8B6F47]">Planning facts</h4>
        <dl className="mt-2 space-y-2 text-sm leading-6 text-[#5A5A5A]">
          {stop.planningFacts.map(fact => (
            <div key={fact.key}>
              <dt className="font-semibold text-[#2C3E3A]">{fact.key}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-[#8B6F47]">Verify state</h4>
        <div className="mt-2 space-y-2 text-sm leading-6 text-[#5A5A5A]">
          <p>{stop.dossierState}</p>
          {stop.conditionalUncertaintyNotes.map(note => (
            <p key={note.id}>{note.text}</p>
          ))}
        </div>
      </section>
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-[#8B6F47]">Provenance</h4>
        <div className="mt-2 space-y-2 text-sm leading-6 text-[#5A5A5A]">
          <p>{stop.provenance}</p>
          <p>{stop.publicationStatus}</p>
        </div>
      </section>
    </div>
  );
}

function StopCard({ stop, isAdded, onToggleAdded, showAdmin }) {
  const guestUncertaintyNotes = getGuestUncertaintyNotes(stop);

  return (
    <article
      className={`rounded-lg border bg-white shadow-sm ${
        isAdded ? 'border-[#8B6F47]/30' : 'border-[#E5DFD6]'
      }`}
    >
      <div className="px-4 py-4 sm:px-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#8B6F47]">
                {stop.daypart}
              </span>
            </div>
            <h2 className="mt-1 text-base font-semibold leading-snug text-[#2C3E3A] sm:text-lg">
              {stop.title}
            </h2>
          </div>
          <ActionButton
            icon={isAdded ? Check : CirclePlus}
            pressed={isAdded}
            onClick={() => onToggleAdded(stop.id)}
          >
            {isAdded ? 'Added' : 'Add'}
          </ActionButton>
        </div>

        <FitCopy sentences={stop.fitSentences} />

        <QuietLine label="Heads up">{stop.headsUp}</QuietLine>

        {stop.safetyFacts.map(fact => (
          <QuietLine key={fact} label="Safety" tone="safety">
            {fact}
          </QuietLine>
        ))}

        {guestUncertaintyNotes.map(note => (
          <QuietLine key={note} label="Double-check">
            {note}
          </QuietLine>
        ))}

        <div className="mt-4 flex flex-wrap gap-2">
          {stop.mapAvailable && <ActionButton icon={MapPinned}>Map</ActionButton>}
          {stop.callAvailable && <ActionButton icon={Phone}>Call</ActionButton>}
          {stop.websiteAvailable && <ActionButton icon={ExternalLink}>Website</ActionButton>}
        </div>

        {showAdmin && <AdminDetails stop={stop} />}
      </div>
    </article>
  );
}

function AdminOverview({ selectedTheme }) {
  const activeTheme = THEME_TAGS.find(tag => tag.id === selectedTheme);

  return (
    <section className="rounded-lg border border-[#E5DFD6] bg-white p-4 shadow-sm">
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#8B6F47]">
            <Info className="h-3.5 w-3.5" />
            Fixture contract
          </h3>
          <p className="mt-2 text-sm leading-6 text-[#5A5A5A]">
            Guest copy is represented as sentence-length placeholders. Source facts,
            verify state, provenance, and publication status stay behind this admin toggle.
          </p>
        </div>
        <div>
          <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#8B6F47]">
            <FileText className="h-3.5 w-3.5" />
            Action spec found
          </h3>
          <AdminFactList items={ACTION_SPEC_SOURCES} />
        </div>
        <div>
          <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#8B6F47]">
            <ShieldAlert className="h-3.5 w-3.5" />
            Visible rules
          </h3>
          <ul className="mt-2 space-y-2 text-sm leading-6 text-[#5A5A5A]">
            <li>Safety renders only when a card has a safety fact.</li>
            <li>Dog policy uncertainty is hidden because this brief has no dog.</li>
            <li>Thin dossiers surface as a deliberate double-check note.</li>
            <li>Selected theme for this fixture: {activeTheme?.label}.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

export default function GuestDaySlideFixtureView() {
  const [selectedTheme, setSelectedTheme] = useState(THEME_TAGS[0].id);
  const [showAdmin, setShowAdmin] = useState(false);
  const [addedStops, setAddedStops] = useState(() =>
    new Set(STOPS.filter(stop => stop.isInitiallyAdded).map(stop => stop.id))
  );

  const selectedTab = useMemo(() => DAY_TABS.find(tab => tab.id === 'sat'), []);
  const background = getGuestDayBackground({ season: BRIEF.season, dayId: selectedTab?.id });

  const toggleAdded = stopId => {
    setAddedStops(current => {
      const next = new Set(current);
      if (next.has(stopId)) next.delete(stopId);
      else next.add(stopId);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] text-[#2C3E3A]">
      <div className="bg-[#1A2F2A] text-white">
        <div className="mx-auto max-w-4xl px-4 pb-4 pt-4 sm:px-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <button
              type="button"
              className="inline-flex h-9 items-center gap-2 rounded-full border border-white/15 px-3 text-xs font-semibold text-white/80"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Plan
            </button>
            <button
              type="button"
              onClick={() => setShowAdmin(value => !value)}
              className="inline-flex h-9 items-center rounded-full border border-white/15 px-3 text-xs font-semibold text-white/80"
            >
              {showAdmin ? 'Hide admin' : 'Admin'}
            </button>
          </div>

          <div className="px-1 py-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#C4B5A0]">
                Pocket Plan
              </p>
              <span className="rounded-full bg-[#8B6F47]/25 px-2 py-1 text-[11px] font-semibold text-[#D4A574]">
                Live fixture
              </span>
            </div>
            <h1 className="mt-2 text-xl font-semibold leading-tight text-white sm:text-2xl">
              {BRIEF.tripName}
            </h1>
            <p className="mt-1 text-xs leading-5 text-white/60 sm:text-sm">
              {BRIEF.area} | {BRIEF.party} | {BRIEF.pace}
            </p>
            <p className="mt-4 text-base font-medium leading-7 text-white sm:text-lg">
              {BRIEF.tripTheme}
            </p>
          </div>

          <div className="mt-3 flex gap-2">
            {DAY_TABS.map(tab => {
              const selected = selectedTab?.id === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={`h-9 flex-1 rounded-full border text-xs font-semibold transition-colors ${
                    selected
                      ? 'border-[#8B6F47] bg-[#8B6F47] text-white'
                      : 'border-white/15 bg-white/10 text-white/70'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <main className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${background.url})` }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[#1A2F2A]/48"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-[#1A2F2A]/65 via-[#1A2F2A]/28 to-[#1A2F2A]/42"
        />
        <div className="relative mx-auto max-w-4xl space-y-4 px-4 py-5 sm:px-6">
          <section className="space-y-3">
            <div className="space-y-1 text-lg font-semibold leading-7 text-white drop-shadow-sm">
              {BRIEF.dayThemeLine.map(line => <p key={line}>{line}</p>)}
            </div>
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#F4E6CF]">
                Re-theme this day
              </p>
              <ThemeTags selectedTheme={selectedTheme} onSelectTheme={setSelectedTheme} />
            </div>
          </section>

          {showAdmin && <AdminOverview selectedTheme={selectedTheme} />}

          <section aria-label={selectedTab?.title ?? 'Selected day'} className="space-y-3">
            {STOPS.map(stop => (
              <StopCard
                key={stop.id}
                stop={stop}
                isAdded={addedStops.has(stop.id)}
                onToggleAdded={toggleAdded}
                showAdmin={showAdmin}
              />
            ))}
          </section>

          <p className="pb-4 text-center text-xs text-[#6F5638]">
            Coming with Pocket Companion
          </p>
        </div>
      </main>
    </div>
  );
}
